import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { links } from '@/config/links';
import { useCompact } from '@/hooks/use-compact';
import { useCookieDialogOpen } from '@/hooks/use-cookie-dialog-open';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Phone, XIcon } from 'lucide-react';

import { useWhoami } from '@/features/auth/hooks/use-whoami-query';

import { Max } from '@/components/contacts-icons/max';
import { Telegram } from '@/components/contacts-icons/telegram';
import { Vkontakte } from '@/components/contacts-icons/vkontakte';
import { Logo } from '@/components/logo/logo';

import { Button } from '@/lib/shadcn/components/ui/button/button';
import { MainButton } from '@/lib/shadcn/components/ui/main-button/main-button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/lib/shadcn/components/ui/sheet';
import { SquareButton } from '@/lib/shadcn/components/ui/square-button/square-button';
import { cn } from '@/lib/shadcn/lib/utils';

import { SideMenuUserButton } from './side-menu-user-button';

export interface SideMenuProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  triggerClassName?: string;
  onBookBuild?: () => void;
  onOpenContacts?: () => void;
}

interface MenuNavItem {
  id: string;
  label: string;
  type: 'scroll' | 'page' | 'external';
  sectionId?: string;
  landingTarget?: string;
  appPath?: string;
  href: string;
  external?: boolean;
}

export function SideMenu({
  open: controlledOpen,
  onOpenChange,
  trigger,
  triggerClassName,
  onBookBuild,
  onOpenContacts,
}: SideMenuProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isCompact = useCompact();
  const openCookieDialog = useCookieDialogOpen((s) => s.open);

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen === undefined ? uncontrolledOpen : controlledOpen;

  const handleOpenChange = (nextOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(nextOpen);
    } else {
      setUncontrolledOpen(nextOpen);
    }
  };

  const handleClose = () => {
    handleOpenChange(false);
  };

  const { data: whoamiData } = useWhoami({
    params: {},
  });

  const roles = whoamiData?.auth.roles ?? [];
  const canAccessBackoffice =
    roles.includes('admin') || roles.includes('service_manager');

  const isLanding = location.pathname === '/' || location.pathname === '';

  const handleNavClick = (
    event: MouseEvent<HTMLAnchorElement>,
    item: {
      type: 'scroll' | 'page' | 'external';
      sectionId?: string;
      landingTarget?: string;
      appPath?: string;
    },
  ) => {
    if (item.type === 'external') {
      handleClose();
      return;
    }

    if (item.sectionId === 'contacts' && isLanding && onOpenContacts) {
      event.preventDefault();
      handleClose();
      onOpenContacts();
      return;
    }

    if (item.sectionId && isLanding) {
      const target = document.querySelector<HTMLElement>(`#${item.sectionId}`);
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        handleClose();
        return;
      }
    }

    if (!isLanding && item.appPath) {
      event.preventDefault();
      handleClose();
      navigate({ href: item.appPath });
      return;
    }

    if (!isLanding && item.landingTarget) {
      event.preventDefault();
      handleClose();
      globalThis.location.href = item.landingTarget;
      return;
    }

    handleClose();
  };

  const menuNavItems: MenuNavItem[] = [
    {
      id: 'scenarios',
      label: t('landing.navigation.scenarios'),
      type: 'scroll' as const,
      sectionId: 'scenarios',
      landingTarget: '/#scenarios',
      appPath: '/#scenarios',
      href: isLanding ? '#scenarios' : '/#scenarios',
    },
    {
      id: 'tours',
      label: t('landing.navigation.tours'),
      type: isLanding ? ('scroll' as const) : ('page' as const),
      sectionId: 'tours',
      landingTarget: '/#tours',
      appPath: '/all-excursions',
      href: isLanding ? '#tours' : '/all-excursions',
    },
    {
      id: 'stay',
      label: t('landing.navigation.stay'),
      type: 'scroll' as const,
      sectionId: 'stay',
      landingTarget: '/#stay',
      appPath: '/#stay',
      href: isLanding ? '#stay' : '/#stay',
    },
    {
      id: 'about',
      label: t('landing.navigation.about'),
      type: 'scroll' as const,
      sectionId: 'about',
      landingTarget: '/#about',
      appPath: '/#about',
      href: isLanding ? '#about' : '/#about',
    },
    {
      id: 'contacts',
      label: t('landing.navigation.contacts'),
      type: 'scroll' as const,
      sectionId: 'contacts',
      landingTarget: '/#contacts',
      appPath: '/#contacts',
      href: isLanding ? '#contacts' : '/#contacts',
    },
    {
      id: 'docs',
      label: t('landing.navigation.docs'),
      type: 'page' as const,
      appPath: '/docs/',
      href: '/docs/',
    },
  ];

  const renderContent = (isMobileView: boolean) => (
    <div className="box-border flex h-full flex-col bg-white">
      {/* Шапка меню (зафиксирована; на мобилке полностью совпадает с LandingHeader) */}
      <div
        className={cn(
          'w-full shrink-0 bg-white',
          isMobileView
            ? 'border-b border-slate-200 pt-[env(safe-area-inset-top,0px)]'
            : 'pt-3',
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between px-4',
            isMobileView ? 'h-[68px] py-1' : 'h-11',
          )}
        >
          <a
            href="/"
            onClick={(e) => {
              if (isLanding) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              handleClose();
            }}
            className="flex shrink-0 items-center rounded-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            aria-label={t('landing.brand')}
          >
            <Logo
              className={cn(
                'w-auto text-slate-500',
                isMobileView
                  ? 'h-[24px] min-[360px]:h-[26px] min-[390px]:h-[28px]'
                  : 'h-[28px]',
              )}
            />
          </a>
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('landing.header.closeMenu')}
            className={cn(
              'flex shrink-0 cursor-pointer items-center justify-center rounded-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
              isMobileView
                ? 'size-10 bg-teal-50 text-teal-500 hover:bg-teal-100/60 active:bg-teal-200 min-[360px]:size-11'
                : 'size-11 bg-slate-100 text-slate-500 hover:bg-slate-200 active:bg-slate-250',
            )}
          >
            <XIcon className="size-5" />
          </button>
        </div>
      </div>

      {/* Основной контент меню */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col px-4',
          isMobileView
            ? 'pb-[calc(16px+env(safe-area-inset-bottom,0px))] pt-3'
            : 'pb-4 pt-3',
        )}
      >
        {/* Профиль / Вход (зафиксирован) */}
        <div className="shrink-0">
          <SideMenuUserButton onClose={handleClose} />
        </div>

        {/* Контейнер навигации (адаптируется под высоту экрана, скроллится при переполнении) */}
        <nav className="my-2 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pr-1">
          {menuNavItems.map((item) => (
            <a
              key={item.id}
              href={item.href}
              {...(item.external ? { target: '_blank', rel: 'noreferrer' } : {})}
              onClick={(e) => handleNavClick(e, item)}
              className="flex min-h-[52px] shrink-0 cursor-pointer items-center border-b border-slate-150 text-[18px] font-semibold tracking-[-0.02em] text-slate-500 transition-colors hover:text-teal-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:text-teal-400"
            >
              {item.label}
            </a>
          ))}
          <button
            type="button"
            onClick={() => {
              handleClose();
              openCookieDialog();
            }}
            className="flex min-h-[52px] w-full shrink-0 cursor-pointer items-center border-b border-slate-150 text-left text-[18px] font-semibold tracking-[-0.02em] text-slate-500 transition-colors hover:text-teal-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:text-teal-400"
          >
            {t('cookie-dialog.menu-item')}
          </button>
        </nav>

        {/* Футер меню: контакты и кнопка "Собрать поездку" (зафиксирован) */}
        <div className="flex shrink-0 flex-col gap-2 pt-3">
          <div className="flex items-center gap-1.5 min-[360px]:gap-2">
            <Button
              asChild
              variant="tonal"
              size="headerIcon"
              className="size-11 shrink-0 rounded-large transition-colors min-[360px]:size-12"
            >
              <a
                href={links.vk}
                target="_blank"
                rel="noreferrer"
                title={t('landing.footer.vkTitle')}
                aria-label={t('landing.footer.vkTitle')}
              >
                <Vkontakte className="size-5 min-[360px]:size-6" />
              </a>
            </Button>
            {links.max ? (
              <Button
                asChild
                variant="tonal"
                size="headerIcon"
                className="size-11 shrink-0 rounded-large transition-colors min-[360px]:size-12"
              >
                <a
                  href={links.max}
                  target="_blank"
                  rel="noreferrer"
                  title={t('landing.footer.maxTitle')}
                  aria-label={t('landing.footer.maxTitle')}
                >
                  <Max className="size-5 min-[360px]:size-6" />
                </a>
              </Button>
            ) : (
              <Button
                variant="tonal"
                size="headerIcon"
                disabled
                title={t('landing.footer.maxUnavailable')}
                aria-label={t('landing.footer.maxTitle')}
                className="size-11 shrink-0 cursor-not-allowed rounded-large opacity-60 min-[360px]:size-12"
              >
                <Max className="size-5 min-[360px]:size-6" />
              </Button>
            )}
            <Button
              asChild
              variant="tonal"
              size="headerIcon"
              className="size-11 shrink-0 rounded-large transition-colors min-[360px]:size-12"
            >
              <a
                href={links.telegram}
                target="_blank"
                rel="noreferrer"
                title={t('landing.footer.telegramTitle')}
                aria-label={t('landing.footer.telegramTitle')}
              >
                <Telegram className="size-5 min-[360px]:size-6" />
              </a>
            </Button>
            <a
              href={links.phone}
              className="flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center gap-1 rounded-large bg-slate-100 px-1.5 text-[11px] font-semibold tracking-tight text-slate-500 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-slate-250 min-[340px]:text-[12px] min-[360px]:h-12 min-[360px]:gap-1.5 min-[360px]:px-2 min-[360px]:text-[13px] min-[390px]:px-2.5 min-[390px]:text-[14px] min-[390px]:tracking-[-0.02em] min-[420px]:text-[15px]"
            >
              <Phone className="size-3.5 shrink-0 text-teal-500 min-[360px]:size-4" />
              <span className="whitespace-nowrap">{links.phoneDisplay}</span>
            </a>
          </div>

          {canAccessBackoffice ? (
            <MainButton
              asChild
              size="s"
              variant="outlined"
              className="w-full font-semibold"
              data-testid="backoffice-button"
            >
              <a href="/backoffice/">{t('side-menu.backoffice')}</a>
            </MainButton>
          ) : null}

          <MainButton
            onClick={() => {
              handleClose();
              if (onBookBuild) onBookBuild();
            }}
            size="l"
            className="h-[54px] w-full font-semibold"
          >
            {t('landing.header.buildTrip')}
          </MainButton>
        </div>
      </div>
    </div>
  );

  if (isCompact) {
    return (
      <>
        {trigger !== null && (
          <div onClick={() => handleOpenChange(true)} className="inline-block">
            {trigger ?? (
              <SquareButton className={triggerClassName} data-testid="menu-button">
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="2"
                  className="size-5"
                >
                  <path d="M3 6h18M3 12h18M3 18h18" />
                </svg>
              </SquareButton>
            )}
          </div>
        )}
        <MobileSlideMenu isOpen={isOpen} onClose={handleClose}>
          {renderContent(true)}
        </MobileSlideMenu>
      </>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      {trigger !== null && (
        <SheetTrigger asChild>
          {trigger ?? (
            <SquareButton className={triggerClassName} data-testid="menu-button">
              <svg
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
                className="size-5"
              >
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </SquareButton>
          )}
        </SheetTrigger>
      )}
      <SheetContent
        side="right"
        className="flex h-full w-[420px] max-w-[420px] flex-col rounded-none border-l border-slate-200 bg-white !p-0"
      >
        <SheetTitle className="sr-only">{t('side-menu.title')}</SheetTitle>
        <SheetDescription className="sr-only">
          {t('side-menu.description')}
        </SheetDescription>
        {renderContent(false)}
      </SheetContent>
    </Sheet>
  );
}

function MobileSlideMenu({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [animOpen, setAnimOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      let innerFrame: number;
      const frame = requestAnimationFrame(() => {
        innerFrame = requestAnimationFrame(() => {
          setAnimOpen(true);
        });
      });
      return () => {
        cancelAnimationFrame(frame);
        cancelAnimationFrame(innerFrame);
      };
    } else {
      setAnimOpen(false);
    }
  }, [isOpen]);

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('side-menu.title')}
      onKeyDown={handleKeyDown}
      className={`guide-landing-portal landing-mobile-menu-drawer ${animOpen ? 'is-open' : ''}`}
    >
      {children}
    </div>,
    document.body,
  );
}
